export interface IBuildPortalUrlConfig {

  /** Apex host, e.g. "dev.customer.portal.davaco.cloud". */
  baseHost: string;

  /** The host currently serving the page (window.location.hostname). */
  currentHost: string;

  /** The current origin, used only for the local-dev form. */
  origin: string;
}

/**
 * Where "Continue" on the unknown-slug fallback should send the browser.
 *
 * Deployed, an organization is a subdomain, so the destination is a different
 * host: `https://<slug>.<baseHost>/`.
 *
 * On localhost there are no subdomains — `resolveTenantContext` reads the
 * tenant from `?org=<slug>` instead. Sending a developer to
 * `https://starbucks.dev.customer.portal.davaco.cloud` would walk them out of
 * the app they are running, so the local form of the same navigation is
 * `<origin>/?org=<slug>`. Mirrors the escape hatch the tenant resolver and the
 * Auth0 redirect URI already use, so the fallback is exercisable in dev.
 *
 * Pure, and takes the host it is told rather than reading `window`, so the
 * branch that only ever runs in dev is the one that can actually be tested.
 */
export function buildPortalUrl(slug: string, config: IBuildPortalUrlConfig): string {
  const base: string = config.baseHost.trim()
.toLowerCase();
  const host: string = config.currentHost.trim()
.toLowerCase();

  const servedFromBase: boolean = host === base || host.endsWith(`.${base}`);

  return servedFromBase
    ? `https://${slug}.${base}/`
    : `${config.origin}/?org=${encodeURIComponent(slug)}`;
}

/**
 * Where the "Continue with Microsoft" option on the unknown-slug fallback
 * should send the browser: the Davaco staff door on the APEX host.
 *
 * It has to be a navigation off this host, not a local `provider.login()`. The
 * fallback only ever renders on a customer-shaped host, so the injected
 * provider there is the Auth0 one — and on a slug with no organization it
 * refuses to start a login at all (`assertOrganizationResolved`). Staff who
 * mistyped a customer address need the Entra door, and the Entra door is
 * whichever host resolves to the apex.
 */
export function buildStaffDoorUrl(config: IBuildPortalUrlConfig): string {
  const base: string = config.baseHost.trim()
.toLowerCase();
  const host: string = config.currentHost.trim()
.toLowerCase();

  const servedFromBase: boolean = host === base || host.endsWith(`.${base}`);

  // Dropping ?org= is the whole point of the local form: resolveTenantContext
  // falls back to the Entra apex context when no override is present.
  return servedFromBase ? `https://${base}/login` : `${config.origin}/login`;
}
